// packages/character-render/src/humanoidMaterials.ts
//
// Shared GPU resources for every <HumanoidMesh>: one bevelled unit box and one
// MeshStandardMaterial per surface (roughness/metalness/emissive). Colours are
// per instance (instanceColor), so a whole crowd shares a handful of programs
// and materials. Created lazily, never disposed (they are process-wide).
import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js';
import type { SurfaceKey } from './humanoidRig';

/** Edge radius of the bevelled unit box (fraction of the box size). */
export const HUMANOID_BEVEL_RADIUS = 0.1;

let bevelledBox: THREE.BufferGeometry | null = null;
let sharpBox: THREE.BufferGeometry | null = null;

/** Unit box with softly rounded edges; the bevel catches light so seams read clean. */
export function getHumanoidBoxGeometry(bevel = true): THREE.BufferGeometry {
  if (bevel) {
    bevelledBox ??= new RoundedBoxGeometry(1, 1, 1, 2, HUMANOID_BEVEL_RADIUS);
    return bevelledBox;
  }
  sharpBox ??= new THREE.BoxGeometry(1, 1, 1);
  return sharpBox;
}

const materials = new Map<string, THREE.MeshStandardMaterial>();

export function surfaceKeyId(s: SurfaceKey): string {
  return `${s.roughness}|${s.metalness}|${s.emissive ?? '-'}`;
}

/** Shared material for a surface. White base colour: instanceColor carries the tint. */
export function getHumanoidMaterial(surface: SurfaceKey): THREE.MeshStandardMaterial {
  const id = surfaceKeyId(surface);
  let m = materials.get(id);
  if (!m) {
    m = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      roughness: surface.roughness,
      metalness: surface.metalness,
    });
    if (surface.emissive) {
      m.emissive = new THREE.Color(surface.emissive);
      m.emissiveIntensity = 1.2;
    }
    m.name = `humanoid:${id}`;
    materials.set(id, m);
  }
  return m;
}
